import type {
    AnswerState,
    ForkedAnswerState,
    LinearAnswerState,
    StateGetter,
    StateSetter,
} from '../../state-types';
import { pathOr } from '../utils/path-or';
import { addAnswer } from './add-answer';
import { deleteAnswer } from './delete-answer';
import { getCardAnswerIds } from './get-card-answer-ids';
import { getCardAnswers } from './get-card-answers';
import { moveAnswer } from './move-answer';
import { toggleAnswerIsFreeForm } from './toggle-answer-is-free-form';
import { updateAnswer } from './update-answer';

export const answersActions = (set: StateSetter, get: StateGetter) => {
    return {
        /** Get an Answer State by id. Should be used with `useShallow` in React components */
        getAnswer: (answerId: string) => get().answers[answerId],

        /** Get the form type of the Question Card */
        getAnswerIsFreeForm: (answerId: string) =>
            pathOr<LinearAnswerState['isFreeForm']>(
                get(),
                ['answers', answerId, 'isFreeForm'],
                undefined
            ),

        /** Get the form type of the Forked Card */
        getAnswerResultCard: (answerId: string) =>
            pathOr<ForkedAnswerState['resultCard']>(
                get(),
                ['answers', answerId, 'resultCard'],
                undefined
            ),

        /** Get all Answers for a Card by cardId */
        getCardAnswers: (cardId: string) => getCardAnswers(cardId)(get()),

        /** Get all Answer Ids for a Card by cardId. Should be used with `useShallow` in React components */
        getCardAnswerIds: (cardId: string) => getCardAnswerIds(cardId)(get()),

        /** Add a new answer to a card based on the card type */
        addAnswer: (cardId: string, partialAnswer: Partial<AnswerState> = {}) =>
            set(addAnswer(cardId, partialAnswer)(get()), true),

        /** Update an answer partially */
        updateAnswer: (answerId: string, partialAnswer: Partial<AnswerState>) =>
            set(updateAnswer(answerId, partialAnswer), true),

        /** Delete an answer */
        deleteAnswer: (cardId: string, answerId: string) =>
            set(deleteAnswer(cardId, answerId), true),

        /** Move an answer into a new index in a card */
        moveAnswer: (cardId: string, answerId: string, newIndex: number) =>
            set(moveAnswer(cardId, answerId, newIndex), true),

        /** Toggle answer's `freeForm` */
        toggleAnswerIsFreeForm: (answerId: string) =>
            set(toggleAnswerIsFreeForm(answerId), true),
    };
};
